import { ReactNode } from "react";
import { Check, X } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Separator } from "@/shared/ui/separator";
import { useTranslation } from "@/shared/i18n";

import {
  RichText,
  TutorialLayout,
  TutorialScreenshot,
  TutorialSectionHeader,
  TutorialTip,
} from "../components/TutorialBlocks";
import { tutorialImage } from "../lib/tutorialImages";

const TOC_ITEMS = [
  { key: "howItWorks", anchor: "como-funciona" },
  { key: "rules", anchor: "regras" },
  { key: "example1", anchor: "exemplo-1" },
  { key: "example2", anchor: "exemplo-2" },
  { key: "checklist", anchor: "checklist" },
];

const EXAMPLE_1_ROWS = [
  { key: "initial", image: "guide/graphic-2.webp" },
  { key: "step1", image: "guide/graphic-1.webp" },
  { key: "step2", image: "guide/graphic-3.webp" },
  { key: "step3", image: "guide/graphic-4.webp" },
  { key: "step4", image: "guide/graphic-5.webp" },
  { key: "step5", image: "guide/graphic-6.webp" },
  { key: "result", image: "guide/graphic-7.webp" },
];

const EXAMPLE_2_SOLUTION_ROWS = [
  { key: "step1", image: "guide/graphic-11.webp" },
  { key: "step2", image: "guide/graphic-12.webp" },
  { key: "step3", image: "guide/graphic-14.webp" },
  { key: "result", image: "guide/graphic-15.webp" },
];

interface ExampleBlockProps {
  tag: string;
  title: string;
  children: ReactNode;
}

function ExampleBlock({ tag, title, children }: ExampleBlockProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex items-center gap-3 border-b border-border bg-muted/50 px-4 py-3">
        <Badge variant="outline" className="border-primary/40 font-mono text-xs uppercase tracking-wider text-primary">
          {tag}
        </Badge>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      <div className="space-y-5 p-5">{children}</div>
    </div>
  );
}

interface ExampleRowProps {
  baseKey: string;
  image: string;
}

function ExampleRow({ baseKey, image }: ExampleRowProps) {
  const { t } = useTranslation("psdTutorials");
  return (
    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
      <div>
        <div className="mb-1.5 font-mono text-xs uppercase tracking-wider text-primary">
          {t(`${baseKey}.label`)}
        </div>
        <h4 className="mb-1.5 text-sm font-semibold text-foreground">{t(`${baseKey}.title`)}</h4>
        <p className="text-sm text-muted-foreground">
          <RichText i18nKey={`${baseKey}.body`} />
        </p>
      </div>
      <TutorialScreenshot
        src={tutorialImage(image)}
        alt={t(`${baseKey}.title`)}
        caption={t(`${baseKey}.caption`)}
      />
    </div>
  );
}

export default function TutorialPsdGuidePage() {
  const { t } = useTranslation("psdTutorials");

  return (
    <TutorialLayout title={t("guide.pageTitle")} subtitle={t("guide.subtitle")}>
      <div className="space-y-10 pb-12">
        <nav className="rounded-lg border border-border border-l-4 border-l-primary bg-card px-6 py-5">
          <div className="mb-3 font-mono text-xs uppercase tracking-widest text-primary">
            {t("guide.toc.title")}
          </div>
          <ol className="list-decimal space-y-1.5 pl-5">
            {TOC_ITEMS.map((item) => (
              <li key={item.key}>
                <a
                  href={`#${item.anchor}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {t(`guide.toc.${item.key}`)}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <section>
          <TutorialSectionHeader id="como-funciona" label="01" title={t("guide.howItWorks.title")} />
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <RichText i18nKey="guide.howItWorks.p1" />
            </p>
            <p>
              <RichText i18nKey="guide.howItWorks.p2" />
            </p>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-emerald-500">
                {t("guide.howItWorks.canTitle")}
              </div>
              <ul className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                    {t(`guide.howItWorks.can${i}`)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4">
              <div className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-red-500">
                {t("guide.howItWorks.cantTitle")}
              </div>
              <ul className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                    {t(`guide.howItWorks.cant${i}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-4">
            <TutorialTip>
              <RichText i18nKey="guide.howItWorks.tip" />
            </TutorialTip>
          </div>
        </section>

        <Separator />

        <section>
          <TutorialSectionHeader id="regras" label="02" title={t("guide.rules.title")} />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-start gap-4 rounded-lg border border-border bg-card px-5 py-4"
              >
                <div className="min-w-7 pt-0.5 text-lg font-semibold text-primary">{i}</div>
                <div>
                  <div className="mb-1 text-sm font-semibold text-foreground">
                    <RichText i18nKey={`guide.rules.rule${i}Title`} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <RichText i18nKey={`guide.rules.rule${i}Body`} />
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <TutorialTip variant="warning">
              <RichText i18nKey="guide.rules.warning" />
            </TutorialTip>
          </div>
        </section>

        <Separator />

        <section>
          <TutorialSectionHeader id="exemplo-1" label="03" title={t("guide.example1.title")} />
          <p className="mb-5 text-sm text-muted-foreground">{t("guide.example1.intro")}</p>
          <ExampleBlock tag={t("guide.problemTag")} title={t("guide.example1.problemTitle")}>
            {EXAMPLE_1_ROWS.map((row) => (
              <ExampleRow key={row.key} baseKey={`guide.example1.${row.key}`} image={row.image} />
            ))}
          </ExampleBlock>
        </section>

        <Separator />

        <section>
          <TutorialSectionHeader id="exemplo-2" label="04" title={t("guide.example2.title")} />
          <p className="mb-5 text-sm text-muted-foreground">{t("guide.example2.intro")}</p>

          <ExampleBlock tag={t("guide.problemTag")} title={t("guide.example2.problemTitle")}>
            <ExampleRow baseKey="guide.example2.initial" image="guide/graphic-8.webp" />
            <TutorialScreenshot
              src={tutorialImage("guide/graphic-9.webp")}
              alt={t("guide.example2.initial.title")}
              caption={t("guide.example2.overviewCaption")}
            />
          </ExampleBlock>

          <div className="mt-5">
            <ExampleBlock tag={t("guide.solutionTag")} title={t("guide.example2.solutionTitle")}>
              <p className="text-sm text-muted-foreground">
                <RichText i18nKey="guide.example2.solutionIntro" />
              </p>
              {EXAMPLE_2_SOLUTION_ROWS.map((row) => (
                <ExampleRow key={row.key} baseKey={`guide.example2.${row.key}`} image={row.image} />
              ))}
            </ExampleBlock>
          </div>
        </section>

        <Separator />

        <section>
          <TutorialSectionHeader id="checklist" label="05" title={t("guide.checklist.title")} />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-lg border border-border bg-card px-5 py-4"
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <div>
                  <div className="mb-0.5 text-sm font-semibold text-foreground">
                    {t(`guide.checklist.item${i}Title`)}
                  </div>
                  <p className="text-sm text-muted-foreground">{t(`guide.checklist.item${i}Body`)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </TutorialLayout>
  );
}
